import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';

import { useL10n } from '@uplift/hooks';
import { useTimezone } from '@uplift/contexts';
import { formatUserFullName } from '@uplift/utils';
import { Match, PublicUser, PublicUserProfile } from '@uplift/api';
import { ProviderMedicalRole } from '@uplift/types';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';

import { ProviderAvatar } from '../../../components/provider-card/provider-card-avatar.component';
import { useStyles } from './archived-provider.styles';
import { useAssetQuery } from '@uplift/queries';


export type ProviderCardProps = {
  match: Match;
  provider: PublicUser;
  providerProfile: PublicUserProfile;
};


export const ArchivedProviderCard: React.FC<ProviderCardProps> = ({ match, provider, providerProfile }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const timezone = useTimezone();
  const userName = formatUserFullName({ firstName: provider.firstName, lastName: provider.lastName, preferredFirstName: providerProfile.preferredName });
  const { data: avatarAsset } = useAssetQuery(providerProfile.avatarAssetId);
  const providerType = provider.medicalRole || ProviderMedicalRole.therapist;

  const archivedDate = React.useMemo<string | undefined>(() => {
    if (match.archivedAt) {
      return CalendarDateService
        .date(match.archivedAt)
        .tz(timezone)
        .format(DateFormats['MMM D, YYYY']);
    }
    return undefined;
  }, [ match.archivedAt, timezone ]);

  return (
    <Card square>
      <CardContent className={classes.content}>
        <Box className={classes.info}>
          <ProviderAvatar src={avatarAsset?.url} />
          <Box className={classes.rightBlock}>
            <Typography color="textSecondary" className={classes.fullNameText}>{userName}</Typography>
            <Typography color="textSecondary" className={classes.typeText}>{l10n.resources.provider.types[providerType]}</Typography>
            {archivedDate && (
              <Typography color="textSecondary" className={classes.lastSeenText}>
                {l10n.yourCareTeam.archivedProviders.archivedOn} {archivedDate}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
